import React,{useState} from 'react';
import toString from 'lodash/toString';
import LabelField from '../labelField/LabelField';
import { Button, Textarea, Modal, Select } from 'app/components';
import { MapTrifold, VideoCamera, WarningCircle } from '@phosphor-icons/react';
import { notify } from 'app/components';
import VenueModal from './venueModal';
import MeetingDetailsModal from './meetingDetailsModal';

import './style'
import { EVENT_TYPE_OPTIONS, VISIBILITY_OPTIONS } from '../../../event-create/containers/step1/constants';

const About = (props) => {
  const { 
    onChange, 
    formData,
    isAdmin,
    readOnly,
  } = props;

  const [showModal, setShowModal] = useState(false);
  const [showNameEdit, setShowNameEdit] = useState(false);
  const [showTypeEdit, setShowTypeEdit] = useState(false);
  const [showVenueModal, setShowVenueModal] = useState(false);
  const [showMeetingDetailsModal, setShowMeetingDetailsModal] = useState(false);
  const [name, setName] = useState(formData?.name);
  const [type, setType] = useState(formData?.type);

  const isEditable = () => {
    return isAdmin || (!isAdmin && formData?.is_active === false && readOnly === false);
  };

  const canEditVisibility = () => {
    // Admin can always edit
    if (isAdmin) return true;
    
    // If event is not live, follow normal editing rules
    if (!formData?.is_active) return !readOnly;
    
    // For live events, non-admin users can only change from private to public once
    if (formData?.is_active && formData?.visibility === false) {
      return true;
    }
    
    return false;
  };

  const handleCancel = () => {
    if (showModal) setShowModal(false);
    if (showNameEdit) setShowNameEdit(false);
    if (showTypeEdit) setShowTypeEdit(false);
    if (showVenueModal) {
      setShowVenueModal(false);
    }
    if (showMeetingDetailsModal) {
      setShowMeetingDetailsModal(false);
    }
    setType(formData.type);
  }

  const handleChange = (key = 'visibility', value = 'true') => {
    if (key === 'type') {
      // If changing from offline to online, show meeting details modal
      if (formData.type === 'offline' && value === 'online') {
        setType(value);
        setShowMeetingDetailsModal(true);
      }
      // If changing from online to offline, show venue modal
      else if (formData.type === 'online' && value === 'offline') {
        setType(value);
        setShowVenueModal(true);
      } else {
        setShowTypeEdit(false);
      }
    } else if (key === 'visibility') {
      // For visibility changes, check if it's allowed
      if (canEditVisibility()) {
        onChange({
          ...formData,
          [key]: value,
        }, 'Updated successfully', handleCancel);
      } else {
        notify('error', 'You can only change visibility from private to public once after the event goes live');
        handleCancel();
      }
    } else {
      onChange({
        ...formData,
        [key]: value,
      }, 'Updated successfully', handleCancel);
    }
  }

  const handleVenueModalSave = (updatedData) => {
    onChange({
      ...formData,
      ...updatedData,
      type: type,
    }, 'Updated successfully', () => {
      setShowVenueModal(false);
      setShowTypeEdit(false);
    });
  }

  const handleMeetingDetailsModalSave = (updatedData) => {
    onChange({
      ...formData,
      ...updatedData,
      type: type,
    }, 'Updated successfully', () => {
      setShowMeetingDetailsModal(false);
      setShowTypeEdit(false);
    });
  }

  return (
    <div className='pt-[36px] px-[80px] flex flex-col gap-9'>
      <h1 className='heading-24'>What's your event about?</h1>

      <div className='flex flex-col gap-8'>
        <div className='flex gap-4 justify-between'>
        <LabelField 
          title="Event name" 
          text={formData?.name}
        />
        {(isEditable()) && 
            <Button 
              type="tertiary-text-underlined"
              onClick={() => setShowNameEdit(true)}
            >
              Edit
            </Button>
          }
        </div>
        
        <LabelField 
          title="Event Category" 
          text={formData?.category || formData?.category_label}
        />

        <div className='flex gap-4 justify-between'>
          <LabelField title="Visibility" 
            text={VISIBILITY_OPTIONS.find((item) => item.id === toString(formData?.visibility))?.label}/>
          {canEditVisibility() && 
            <Button 
              type="tertiary-text-underlined"
              onClick={() => setShowModal(true)}
            >
              Edit
            </Button>
          }
        </div>
        <div className='flex gap-4 justify-between'>
        <LabelField 
          title="Event type" 
          text={EVENT_TYPE_OPTIONS.find((item) => item.id === formData?.type)?.label}
        />
        {isEditable() && <Button 
              type="tertiary-text-underlined"
              onClick={() => setShowTypeEdit(true)}
            >
              Edit
            </Button>}
        </div>
      </div>

      {showModal && 
        <Modal size="regular"
          className="modal-delete"
          onClick={handleCancel}>
          <div className="flex flex-col gap-8">
            
            <div className="w-14 h-14 flex flex-col justify-center items-center gap-6 warning-bg">
              <WarningCircle size={32} 
                color='#D92D20'/>
            </div>

            <div className='flex flex-col gap-2'>
              <h1 className='heading-24'>{`Do you want to change visibility to ${toString(formData?.visibility) === 'false' ? 'Public' : 'Private'}?`}</h1>
              {toString(formData?.visibility) === 'false' && <p>This will change who can see and access your event.</p>}
            </div>

            <div className="flex flex-col gap-2 justify-center">
              <Button className="w-full"
                type="primary-black"
                onClick={() => handleChange('visibility', toString(formData?.visibility) === 'false' ? 'true' : 'false')}
              >
                {`Change to ${toString(formData?.visibility) === 'false' ? 'Public' : 'Private'}`}
              </Button>
              <Button className="w-full"
                type="tertiary"
                onClick={handleCancel}
              >
                Cancel
              </Button>
            </div>
          </div>
        </Modal>
      }
      {showNameEdit && (
        <Modal
          size="regular"
          className="modal-name-edit"
          onClickPrimaryCta={() => handleChange('name', name)}
          onClickSecondaryCta={handleCancel}
        >
          <Textarea
          className={`event-name`}
          width="100%" 
          label="Event name"
          charLength={50}
          value={name}
          onChange={(e) => setName(e.target.value)}
        />
        </Modal>
      )}
      {showTypeEdit && (
        <Modal
          size="regular"
          className="modal-type-edit"
          onClickPrimaryCta={() => handleChange('type', type)}
          onClickSecondaryCta={handleCancel}
        >
          <div className='flex flex-col gap-4 justify-center items-center'>
          {EVENT_TYPE_OPTIONS.map((option, id) => (
            <Select
            key={id}
            className="w-full"
            label={option.label}
            icon={id === 0 ? <MapTrifold size={24} /> : <VideoCamera size={24} />}
            selected={type === option.id}
            onClick={() => setType(option.id)}
          />
          ))}
          </div>
        </Modal>
      )}
      {showVenueModal && (
        <VenueModal
          show={showVenueModal}
          onClose={handleCancel}
          formData={formData}
          onChange={handleVenueModalSave}
        />
      )}
      {showMeetingDetailsModal && (
        <MeetingDetailsModal
          show={showMeetingDetailsModal}
          onClose={handleCancel}
          formData={formData}
          onChange={handleMeetingDetailsModalSave}
        />
      )}
    </div>
  );
};

export default About;
